import React, { useEffect, useState } from "react";
// @ts-ignore: Unreachable code error
import crypto from "crypto-browserify";

import {
  FormLabel,
  FormControl,
  Input,
  Button,
  Flex,
  Center,
  Link,
  Text,
  Box,
  FormErrorMessage,
  InputGroup,
  InputRightElement,
  IconButton,
} from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import { Link as RouterLink, Redirect, useHistory } from "react-router-dom";
import { useLoginMutation } from "redux/services/auth";
import * as yup from "yup";
import env from "env";
import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import LoadingOverlay from "components/LoadingOverlay";

const schema = yup.object().shape({
  number: yup.string().email("Invalid email").required("Email is required"),
  password: yup.string().required("Password is required"),
});

const messageWrongEmailPassword =
  "Your Email or Password is incorrect. Please try again.";

const Login: React.FC<{ isEnableOnlyAuthCode?: boolean }> = ({
  isEnableOnlyAuthCode,
}) => {
  const {
    formState: { isSubmitting },
  } = useForm();
  const { push } = useHistory();
  const [login, { isSuccess, isError, error, isLoading }] = useLoginMutation();
  const [showPassword, setShowPassword] = useState(false);

  const params = new URLSearchParams(window.location.search);
  const code = params.get("code");
  const session_state = params.get("session_state");

  const isAutoNavigateWso2Auth =
    isEnableOnlyAuthCode ||
    (env.app.authResourceOwner === "false" &&
      env.app.authCodeEnabled === "true" &&
      !code &&
      !session_state);

  const [formData, setFormData] = useState({
    number: "",
    password: "",
  });

  const [errors, setErrors] = useState<{
    number?: any;
    password?: any;
  }>({});

  useEffect(() => {
    if (isSuccess) {
      push("/");
    } else if (isError) {
      setErrors({
        password: messageWrongEmailPassword,
      });
    }
  }, [isError, isSuccess, push, error]);

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const validateForm = async () => {
    try {
      await schema.validate(formData, { abortEarly: false });
      setErrors({});
      return true;
    } catch (err: any) {
      const validationErrors: any = {};
      err.inner.forEach((error: any) => {
        validationErrors[error.path] = error.message;
      });
      setErrors(validationErrors);
      return false;
    }
  };

  const onSubmit = async () => {
    const isValid = await validateForm();
    if (isValid) {
      login({
        username: formData.number,
        password: formData.password,
        grant_type: "password",
        scope: "openid profile",
      });
    }
  };

  const handleLoginWithIDPs = (): void => {
    const base64ToBase64Url = (base64String: string) => {
      return base64String
        .replace(/=/g, "")
        .replace(/\+/g, "-")
        .replace(/\//g, "_");
    };

    window.localStorage.setItem("token", "");

    const codeVerifier = crypto.randomBytes(32).toString("hex");
    window.localStorage.setItem("code_verifier", codeVerifier);
    const codeChallenge = crypto
      .createHash("sha256")
      .update(codeVerifier)
      .digest("base64");

    const queryParams = {
      response_type: "code",
      client_id: env.app.authCodeAppId,
      redirect_uri: env.app.rootUrl,
      scope: "openid profile",
      code_challenge_method: "S256",
    };

    const urlParams = new URLSearchParams(queryParams);
    const baseUrl = `${env.api.baseUrl.tokenBaseUrl}oauth2/authorize`;
    const finalUrl = `${baseUrl}?${urlParams.toString()}&code_challenge=${base64ToBase64Url(
      codeChallenge
    )}`;

    window.location.href = finalUrl;
  };

  useEffect(() => {
    if (isAutoNavigateWso2Auth) {
      // Delay timeout for logout case
      setTimeout(() => {
        handleLoginWithIDPs();
      }, 1000);
    }
  }, [isAutoNavigateWso2Auth]);

  if (isAutoNavigateWso2Auth) {
    return (
      <Flex
        align="center"
        justify="center"
        position="fixed"
        zIndex={10}
        top="-0.5rem"
        left="0"
        w="100%"
        h="100%"
        background="white"
      >
        <LoadingOverlay />
      </Flex>
    );
  }

  if (code && session_state) {
    return (
      <Redirect
        to={`/auth/authorize?code=${code}&session_state=${session_state}`}
      />
    );
  }

  return (
    <Center
      className="Login"
      w="100%"
      h={["calc(100% - 60px)", null, "100%"]}
      overflow="auto"
      mt="0 !important"
      p={["24px 15px", null, 0]}
    >
      <Flex
        flexDir="column"
        h="100%"
        w={["100%", "364px"]}
        justify={["flex-start", null, "center"]}
      >
        <Text
          textTransform={"uppercase"}
          textAlign="center"
          textStyle="h1"
          mb={"30px"}
        >
          Log in
        </Text>

        {env.app.authResourceOwner === "true" && (
          <form
            aria-label="login-form"
            onSubmit={(e) => {
              e.preventDefault();
              onSubmit();
            }}
          >
            <FormControl isInvalid={errors.number} mb="32px">
              <FormLabel>Email</FormLabel>
              <Input
                name="number"
                data-testid="email-input"
                onChange={handleChange}
              />
              {errors.number && (
                <FormErrorMessage fontSize={12}>
                  {errors.number}
                </FormErrorMessage>
              )}
            </FormControl>
            <FormControl
              isInvalid={errors.password}
              mb={errors.password ? "10px" : "32px"}
            >
              <FormLabel>Password</FormLabel>
              <InputGroup>
                <Input
                  type={showPassword ? "text" : "password"}
                  value={formData?.password}
                  maxLength={12}
                  onChange={handleChange}
                  name="password"
                  data-testid="password-input"
                />
                <InputRightElement>
                  <IconButton
                    data-testid="toggle-show-password"
                    variant="unstyled"
                    onClick={() => setShowPassword(!showPassword)}
                    icon={!showPassword ? <ViewOffIcon /> : <ViewIcon />}
                    aria-label="Toggle Password Visibility"
                  />
                </InputRightElement>
              </InputGroup>

              {errors.password && (
                <FormErrorMessage fontSize={12}>
                  {errors.password}
                </FormErrorMessage>
              )}
            </FormControl>
            <Button
              data-testid="button-login"
              isLoading={isSubmitting || isLoading}
              type="submit"
              variant="solid"
              colorScheme="blue"
              w="100%"
              h="38px"
            >
              <Text textStyle="input-label" fontWeight="600" color={"white"}>
                Log In
              </Text>
            </Button>
          </form>
        )}

        {env.app.authCodeEnabled === "true" && (
          <Button
            variant="solid"
            colorScheme="blue"
            w="100%"
            h="38px"
            mt="15px"
            onClick={handleLoginWithIDPs}
          >
            <Text textStyle="input-label" fontWeight="600" color={"white"}>
              Log In With IDPs
            </Text>
          </Button>
        )}

        {env.app.authResourceOwner === "true" && (
          <>
            <Flex justify="space-between" mt="15px" mb="27px">
              <Link as={RouterLink as any} to="/forgot">
                Forgot Login Details?
              </Link>
            </Flex>
            <Text
              fontSize="12px"
              lineHeight="20px"
              fontWeight="400"
              display={["block", null, "none"]}
              px="5px"
              textAlign="center"
            >
              By logging in, you accept our{" "}
              <Link as={RouterLink as any} to="#" textDecor="underline">
                Security & Privacy Statement.
              </Link>
            </Text>
          </>
        )}

        {/* Fake bottom padding */}
        <Box w="100%" minH="24px" />
      </Flex>
    </Center>
  );
};

export default Login;
